import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as RightArrowIcon } from '../../../../../../../../assets/icons/arrow-right.svg';
import { ReactComponent as CensusSheetIcon } from '../../../../../../../../assets/icons/census-sheet.svg';
import { ReactComponent as ChevronDownIcon } from '../../../../../../../../assets/icons/chevron-down.svg';
import { ReactComponent as CensusCheckGlyph } from '../../../../../../../../assets/icons/status-success.svg';
import { ReactComponent as TrashIcon } from '../../../../../../../../assets/icons/trash-new.svg';
import CustomButton from '../../../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import ExternalBenefitsDocs from '../../../../../../../../shared/components/ExternalBenefits/ExternalBenefitsDocs/ExternalBenefitsDocs';
import Badge from '../../../../../../../../shared/components/Globals/Badge/Badge';
import { Toast } from '../../../../../../../../shared/components/Globals/Toast/Toast';
import ActivationCensusModal, {
  type ActivationCensusApplyPayload
} from '../../../../../../../../shared/components/Modals/ActivationCensusModal/ActivationCensusModal';
import CensusBackgroundStatus from '../../../../../../../../shared/components/Modals/ActivationCensusModal/components/CensusBackgroundStatus/CensusBackgroundStatus';
import PayrollSection, {
  type IPayrollSchedule
} from '../../../../../../../../shared/components/PayrollSection/PayrollSection';
import SetupSection from '../../../../../../../../shared/components/SetupSection/SetupSection';
import SetupSlot from '../../../../../../../../shared/components/SetupSlot/SetupSlot';
import { BENEFITS_DOCUMENTS_SOURCE } from '../../../../../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../../../../../../shared/constants/toast';
import { useActivationCensusFlow } from '../../../../../../../../shared/hooks/useActivationCensusFlow/useActivationCensusFlow';
import { useBroker } from '../../../../../../../../shared/hooks/useBroker/useBroker';
import { useUser } from '../../../../../../../../shared/hooks/useUser/useUser';
import type { IActivationCensusValidationErrorsResult, IQuote } from '../../../../../../../../shared/interfaces/quotes';
import QuotesService from '../../../../../../../../shared/services/api/quotes/quotes';
import { TRANSLATIONS_CLIENTS } from '../../../../../../../../shared/translations/clients';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../../../shared/translations/prospects';
import {
  buildQuoteUpdateBody,
  censusVariantFor,
  isVoluntaryQuote,
  resolveActivationCensusPriceMismatch
} from '../../../../../../../../shared/utils/quotes/quotes';

import './PlanSetupRow.scss';

interface IPlanSetupRowProps {
  quote: IQuote;
  prospectId: number | null;
  isAppOnly?: boolean;
  requiresMinimum?: boolean;
  censusFileName: string | null;
  appOnly?: boolean;
  onApplyCensus: (quoteId: number, filename: string) => void;
  onRemoveCensus: (quoteId: number) => void;
  onActivate: (quoteId: number) => void;
  onQuoteUpdate: (quote: IQuote) => void;
}

const PlanSetupRow = ({
  quote,
  prospectId,
  isAppOnly,
  requiresMinimum,
  censusFileName,
  appOnly = false,
  onApplyCensus,
  onRemoveCensus,
  onActivate,
  onQuoteUpdate
}: Readonly<IPlanSetupRowProps>) => {
  const { t } = useTranslation();
  const { brokerId } = useUser();
  const { commercialAddons, addonTypeById } = useBroker();
  const [expanded, setExpanded] = useState(false);
  const [censusOpen, setCensusOpen] = useState(false);
  const [errorToastOpen, setErrorToastOpen] = useState(false);

  const SETUP = TRANSLATIONS_PROSPECTS.profile.activationSetup;
  const PLANS_IN_SETUP = TRANSLATIONS_CLIENTS.profile.plansInSetup;

  const resolvePriceMismatch = (result: IActivationCensusValidationErrorsResult) =>
    resolveActivationCensusPriceMismatch(quote, commercialAddons, brokerId, result, t);

  const censusFlow = useActivationCensusFlow({
    brokerId,
    prospectId: prospectId ?? 0,
    quoteId: quote.id,
    quoteIds: [quote.id],
    appliedQuoteIds: censusFileName !== null ? [quote.id] : [],
    requiresMinimum,
    variant: censusVariantFor(!!isAppOnly),
    resolvePriceMismatch
  });

  const censusDone = censusFileName !== null;
  const isVoluntary = isVoluntaryQuote(quote, addonTypeById);
  const toggle = () => setExpanded((prev) => !prev);

  const savedPayroll: IPayrollSchedule = {
    frequency: quote.frequency ?? '',
    lastPaymentDate: quote.paymentDate ?? ''
  };

  const handleSavePayroll = async (schedule: IPayrollSchedule) => {
    if (!brokerId || !prospectId) return;
    const updated = await QuotesService.updateQuote(
      brokerId,
      prospectId,
      quote.id,
      buildQuoteUpdateBody(quote, {
        frequency: schedule.frequency || null,
        paymentDate: schedule.lastPaymentDate || null
      })
    );
    onQuoteUpdate(updated);
  };

  const payrollDone = isVoluntary ? Boolean(quote.frequency && quote.paymentDate) : true;
  const totalSteps = isVoluntary ? 2 : 1;
  const doneSteps = (censusDone ? 1 : 0) + (isVoluntary && payrollDone ? 1 : 0);
  const summary =
    doneSteps === totalSteps
      ? t(PLANS_IN_SETUP.summaryComplete)
      : t(PLANS_IN_SETUP.summaryProgress, { done: doneSteps, total: totalSteps });

  const handleApply = async ({ filename }: ActivationCensusApplyPayload) => {
    const distribution = censusFlow.distribution;

    if (!brokerId || !prospectId || !distribution?.length) {
      setErrorToastOpen(true);
      return;
    }

    setErrorToastOpen(false);
    try {
      await QuotesService.applyActivationCensus(brokerId, prospectId, quote.id, { distribution });
      onApplyCensus(quote.id, filename);
      censusFlow.reset();
    } catch {
      setErrorToastOpen(true);
    }
  };

  const handleRemove = () => {
    onRemoveCensus(quote.id);
    censusFlow.reset();
  };

  const showBackgroundStatus = !censusDone && !censusOpen && censusFlow.step !== 'upload';

  const renderCensusBody = () => {
    if (censusDone) {
      return (
        <SetupSlot
          icon={<CensusCheckGlyph width={18} height={18} />}
          description={t(SETUP.census.loaded, { count: quote.workforceSize ?? 0, censusName: censusFileName })}
        >
          <CustomButton variant="default" size="xs" onClick={handleRemove}>
            <TrashIcon className="plan-setup-row__trash" />
            {t(SETUP.census.remove)}
          </CustomButton>
        </SetupSlot>
      );
    }

    if (showBackgroundStatus) {
      return <CensusBackgroundStatus step={censusFlow.step} onOpen={() => setCensusOpen(true)} />;
    }

    return (
      <SetupSlot icon={<CensusSheetIcon />} title={t(SETUP.census.intro)}>
        <CustomButton variant="primary" size="s" onClick={() => setCensusOpen(true)}>
          {t(SETUP.census.upload)}
          <RightArrowIcon className="plan-setup-row__arrow" />
        </CustomButton>
      </SetupSlot>
    );
  };

  return (
    <div className="plan-setup-row">
      <button type="button" className="plan-setup-row__header" aria-expanded={expanded} onClick={toggle}>
        <div className="plan-setup-row__header-main">
          <div className="plan-setup-row__head">
            <span className="plan-setup-row__name">{quote.name}</span>
            <Badge variant={censusDone ? 'success' : 'neutral'}>
              {t(censusDone ? PLANS_IN_SETUP.badgeReady : PLANS_IN_SETUP.badgeInSetup)}
            </Badge>
          </div>
          <span className="plan-setup-row__summary">{summary}</span>
        </div>
        <ChevronDownIcon className={`plan-setup-row__chevron${expanded ? ' plan-setup-row__chevron--open' : ''}`} />
      </button>

      {expanded && (
        <div className="plan-setup-row__body">
          <SetupSection title={t(SETUP.census.title)} done={censusDone}>
            {renderCensusBody()}
          </SetupSection>

          {isVoluntary && <PayrollSection quoteId={quote.id} saved={savedPayroll} onSave={handleSavePayroll} />}

          {appOnly && brokerId && prospectId !== null && (
            <ExternalBenefitsDocs
              layout="summary"
              quote={quote}
              brokerId={brokerId}
              prospectId={prospectId}
              source={BENEFITS_DOCUMENTS_SOURCE.planSetup}
              onSubmitted={onQuoteUpdate}
            />
          )}

          <CustomButton
            className="plan-setup-row__button-activate"
            variant="primary"
            size="s"
            disabled={!censusDone || !payrollDone}
            onClick={() => onActivate(quote.id)}
          >
            {t(PLANS_IN_SETUP.activate)}
            <RightArrowIcon className="plan-setup-row__arrow" />
          </CustomButton>
        </div>
      )}

      <ActivationCensusModal
        open={censusOpen}
        brokerId={brokerId}
        flow={censusFlow}
        quoteName={quote.name}
        isAppOnly={isAppOnly}
        onClose={() => setCensusOpen(false)}
        onApply={handleApply}
      />

      <Toast
        open={errorToastOpen}
        message={t(GENERIC_ERROR_TOAST.message)}
        variant={GENERIC_ERROR_TOAST.variant}
        onClose={() => setErrorToastOpen(false)}
      />
    </div>
  );
};

export default PlanSetupRow;
